import './github/wiki/wiki-edit'
import './github/wiki/gollum'
import './github/wiki/legacy'
import {ASCIIDoc, ASCIIDocHelp} from './github/wiki/editor/langs/asciidoc'
import {GollumEditor, defineHelp, defineLanguage} from './github/wiki/editor/gollum-editor'
import {MarkDown, MarkDownHelp} from './github/wiki/editor/langs/markdown'
import {OrgMode, OrgModeHelp} from './github/wiki/editor/langs/org'
import {Pod, PodHelp} from './github/wiki/editor/langs/pod'
import {Textile, TextileHelp} from './github/wiki/editor/langs/textile'
import {Creole} from './github/wiki/editor/langs/creole'
import {RDoc} from './github/wiki/editor/langs/rdoc'
import {observe} from '@github-ui/selector-observer'
import {on} from 'delegated-events'

observe('#gollum-editor', function (el) {
  GollumEditor({
    NewFile: el.classList.contains('create'),
  })

  toggleMarkdownEditorMode()
})

// Rotates the triangle for table of content headers in the sidebar and toggles visibility of the table of contents
on('click', '.js-wiki-sidebar-toc-toggle-chevron-button', function (event) {
  const button = event.currentTarget

  button
    .querySelector('.js-wiki-sidebar-toc-toggle-chevron')
    ?.classList?.toggle('js-wiki-sidebar-toc-toggle-chevron-open')

  const container = button.closest<HTMLElement>('.js-wiki-sidebar-page-container')
  const tableOfContents = container?.querySelector('.js-wiki-sidebar-toc-container')
  if (!tableOfContents) return

  /* eslint-disable-next-line github/no-d-none -- `d-none` has to be used if visibility is conditional on render */
  tableOfContents.classList.toggle('d-none')

  button.ariaExpanded = button.ariaExpanded === 'true' ? 'false' : 'true'
})

// Rotates the triangle for pages section header in the sidebar
on('click', '.js-wiki-toggle-collapse', function (event) {
  event.currentTarget
    .querySelector('.js-wiki-sidebar-pages-toggle-chevron')
    ?.classList?.toggle('js-wiki-sidebar-pages-toggle-chevron-open')
})

observe('.js-wiki-sidebar-toc-fragment', function (el) {
  function toggleHidden(element: HTMLElement, hidden: boolean) {
    if (hidden) {
      element.setAttribute('hidden', '')
    } else {
      element.removeAttribute('hidden')
    }
  }
  function toggleSpinner(loading: boolean, container: HTMLElement | null) {
    if (container) {
      const chevron = container.querySelector('.js-wiki-sidebar-toc-toggle-chevron') as HTMLElement
      const spinner = container.querySelector('.js-wiki-sidebar-toc-spinner') as HTMLElement

      toggleHidden(chevron, loading)
      toggleHidden(spinner, !loading)
    }
  }

  el.addEventListener('loadstart', e =>
    toggleSpinner(true, (e.currentTarget as HTMLElement).closest<HTMLElement>('.js-wiki-sidebar-page-container')),
  )

  el.addEventListener('include-fragment-replace', e => {
    const currentTarget = e.currentTarget as HTMLElement
    if (!currentTarget) {
      return
    }

    toggleSpinner(false, currentTarget.closest<HTMLElement>('.js-wiki-sidebar-page-container'))

    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    const fragment: HTMLElement | null = (e as any).detail?.fragment
    if (!fragment || fragment.textContent !== '') return

    const container: HTMLElement | null = currentTarget.closest<HTMLElement>('.js-wiki-sidebar-page-container')

    const chevronButton = container?.querySelector('.js-wiki-sidebar-toc-toggle-chevron-button')
    if (!chevronButton) return

    chevronButton.classList.toggle('v-hidden')
  })
})

// Expand/collapse wiki table of contents
on('click', '.js-wiki-toggle-collapse', function (event) {
  const pagesBox = event.currentTarget.closest<HTMLElement>('.js-wiki-pages-box')!
  const toggleDisplays = Array.from(pagesBox.querySelectorAll<HTMLElement>('.js-wiki-sidebar-toggle-display'))

  // Note that toggleDisplays is really only expected to contain one element
  for (const toggleDisplay of toggleDisplays) {
    /* eslint-disable-next-line github/no-d-none */
    toggleDisplay.classList.toggle('d-none')
  }

  // eslint-disable-next-line github/no-d-none
  const isExpanded = toggleDisplays.length > 0 && toggleDisplays.every(el => !el.classList.contains('d-none'))

  const iconButton = pagesBox.querySelector<HTMLElement>('.js-wiki-sidebar-pages-toggle-chevron')
  if (iconButton) iconButton.ariaExpanded = isExpanded ? 'true' : 'false'
})

on('click', '.js-wiki-more-pages-link', function (event) {
  event.preventDefault()
  event.currentTarget.closest<HTMLElement>('.js-wiki-pages-box')!.classList.toggle('wiki-show-more')
})

// Append wiki format to preview request
on('preview:setup', '.js-previewable-comment-form', function (event) {
  const formatSelect = event.currentTarget.querySelector('#wiki_format')
  if (formatSelect) {
    const request = event.detail
    request.data.append('wiki_format', (formatSelect as HTMLInputElement).value)
  }
})

// Re-render the preview when changing format during preview
on('change', '#wiki_format', function (event) {
  const target = event.currentTarget
  const container = target.closest<HTMLElement>('.js-previewable-comment-form')!
  if (container.classList.contains('preview-selected')) {
    container.dispatchEvent(
      new CustomEvent('preview:render', {
        bubbles: true,
        cancelable: false,
      }),
    )
  }

  toggleMarkdownEditorMode()
})

function toggleMarkdownEditorMode() {
  const editor = document.querySelector('#gollum-editor-body')
  const select = document.querySelector<HTMLSelectElement>('#wiki_format')

  editor?.classList.toggle('js-paste-markdown', select?.value === 'markdown')
}

defineHelp('asciidoc', ASCIIDocHelp)
defineHelp('markdown', MarkDownHelp)
defineHelp('org', OrgModeHelp)
defineHelp('pod', PodHelp)
defineHelp('textile', TextileHelp)
defineLanguage('asciidoc', ASCIIDoc)
defineLanguage('creole', Creole)
defineLanguage('markdown', MarkDown)
defineLanguage('org', OrgMode)
defineLanguage('pod', Pod)
defineLanguage('rdoc', RDoc)
defineLanguage('textile', Textile)
